/**
 * DocumentEmissionForm
 * Formulário da emissão: empresa → tipo → alcance → período → bloco de assinaturas → anexos.
 *
 * A empresa vem PRIMEIRO de propósito: `GET /documents/types` filtra os tipos pelo
 * módulo que a empresa contratou. Escolher o tipo antes obrigaria a limpar o campo
 * assim que a empresa mudasse.
 *
 * Ela confere sozinha o que falta para emitir enquanto a pessoa preenche (#604), e é essa
 * conferência que libera os botões de gerar. Os links de resolver abrem em outra aba, para o
 * formulário continuar vivo; na volta, a tela confere de novo e recarrega as opções.
 *
 * @version 2026.1.0
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Description as DocxIcon,
  OpenInNew as AbreEmOutraAbaIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { CompanySelectField } from '../../../components/forms/CompanySelectField';
import { PrerequisiteBlock, type PrerequisiteBlockProps } from '../../../components/common';
import { SignatureBlockSelector } from './SignatureBlockSelector';
import { AttachmentSelector } from './AttachmentSelector';
import { DocumentPreview } from './DocumentPreview';
import { DeclaracoesEpiPanel } from './DeclaracoesEpiPanel';
import { ActionButton } from '../../../components/common';
import { PendencyList } from './PendencyList';
import documentService from '../../../services/documentService';
import departmentService from '../../../services/departmentService';
import occupationService from '../../../services/occupationService';
import employeeService from '../../../services/employeeService';
import { MachineStatus, machineService, type Machine } from '../../../services/machineService';
import type { ConsultaDeclaracoesEpi } from '../../../services/ltcatEpiDeclaracoesService';
import { prerequisiteFromError } from '../../../utils/prerequisiteFromError';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { useAcao } from '../../../authorization/useVeredito';
import { useVerificacaoDaEmissao, type EstadoDaVerificacao } from '../hooks/useVerificacaoDaEmissao';
import { SCOPE_LABELS, type RiskApplicationScope } from '../../../types/riskApplication';
import type {
  DocumentFormat,
  DocumentTypeSummary,
  EmissionRequest,
  PreviewResult,
  ReportType,
  SignatureBlockItem,
} from '../../../types/document';

/** Máquinas ativas do alcance, no tamanho máximo de página da API, até acabarem as páginas. */
const TAMANHO_DA_PAGINA_DE_MAQUINAS = 100;

const carregarMaquinasDoAlcance = async (companyId: number, departmentId: number | null): Promise<Machine[]> => {
  const encontradas: Machine[] = [];
  let pagina = 1;
  let totalDePaginas = 1;
  while (pagina <= totalDePaginas) {
    const resposta = await machineService.getMachines({
      status: MachineStatus.ACTIVE,
      page: pagina,
      limit: TAMANHO_DA_PAGINA_DE_MAQUINAS,
      ...(departmentId !== null && { departmentId }),
    });
    encontradas.push(...resposta.machines);
    totalDePaginas = resposta.totalPages;
    pagina += 1;
  }
  // A API recorta pelas empresas do usuário, não por uma só: quem vê várias recebe todas.
  return encontradas.filter((maquina) => Number(maquina.companyId) === companyId);
};

/** Opção de alvo do alcance (setor, cargo ou funcionário). */
interface ScopeTarget {
  id: number;
  label: string;
}

const mensagemDeErro = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

const AJUDA_HORAS_HOMEM =
  'Opcional. Soma das horas trabalhadas pelos empregados do alcance escolhido, no período, como consta na folha de ponto. É usada na taxa de frequência e na taxa de gravidade do quadro de acidentes. Se ficar vazio, as duas taxas saem "—".';
const MENSAGEM_HORAS_ABAIXO_DO_MINIMO = 'Informe pelo menos 1 hora, ou deixe o campo vazio.';

const AJUDA_MAQUINAS_TODAS =
  'Nenhuma marcada: o laudo inclui todas as máquinas ativas do alcance escolhido. Marque só as que devem entrar para limitar o laudo.';
const AJUDA_MAQUINAS_SELECIONADAS = 'Só as máquinas marcadas entram no laudo.';
const AJUDA_MAQUINAS_SEM_SETOR = 'Máquina sem setor só entra no laudo da empresa inteira.';

const SEM_TIPO_DE_DOCUMENTO =
  'Esta empresa não tem módulo contratado que emita documentos. Peça ao administrador do sistema para habilitar o módulo.';

/** O motivo escrito sob os botões de gerar, por estado da conferência. */
const MOTIVO_DOS_BOTOES: Partial<Record<EstadoDaVerificacao, string>> = {
  incompleto: 'Preencha empresa, documento, alcance e período para ver o que falta.',
  verificando: 'Verificando o que falta para emitir…',
  falhou: 'Não foi possível verificar o que falta agora. A emissão ainda confere o que impede.',
};
const MOTIVO_DO_BLOQUEIO = 'Resolva os itens marcados como impeditivos para liberar a emissão.';

/**
 * O bloqueio que a prévia ou a geração recusou, com quantas respostas a conferência já tinha
 * dado naquela hora: resposta mais nova da conferência fala por cima dele.
 */
interface BloqueioDaAcao {
  bloco: PrerequisiteBlockProps;
  respostasAteEntao: number;
}

/** Os alcances que pedem um alvo. Não existe alcance unidade na emissão. */
type AlcanceComAlvo = 'department' | 'occupation' | 'employee';

/** O que a lista vazia de alvo diz, e onde se cadastra o que falta. */
interface VazioDoAlvo {
  texto: string;
  rotulo: string;
  rota: string;
}

const VAZIO_POR_ALCANCE: Record<AlcanceComAlvo, VazioDoAlvo> = {
  department: {
    texto: 'Esta empresa não tem setores cadastrados.',
    rotulo: 'Cadastrar setor',
    rota: '/departments/create',
  },
  occupation: {
    texto: 'Esta empresa não tem cargos cadastrados.',
    rotulo: 'Cadastrar cargo',
    rota: '/occupations/create',
  },
  employee: {
    texto: 'Esta empresa não tem funcionários cadastrados.',
    rotulo: 'Cadastrar funcionário',
    rota: '/employees/new',
  },
};

/**
 * No alcance `company` o alvo é a própria empresa — não há segunda lista a carregar.
 */
const carregarAlvos = async (
  scope: RiskApplicationScope,
  companyId: number
): Promise<ScopeTarget[]> => {
  if (scope === 'company') return [];

  if (scope === 'department') {
    const setores = await departmentService.getDepartmentsByCompanyId(String(companyId));
    return setores.map((setor) => ({ id: Number(setor.id), label: setor.name }));
  }

  if (scope === 'occupation') {
    const cargos = await occupationService.getOccupations({
      limit: 200,
      filters: { companyId },
    });
    return cargos.data.map((cargo) => ({ id: cargo.id, label: cargo.name }));
  }

  const funcionarios = await employeeService.getEmployeesByCompany(companyId);
  return funcionarios.map((funcionario) => ({ id: funcionario.id, label: funcionario.name }));
};

export const DocumentEmissionForm: React.FC = () => {
  const [companyId, setCompanyId] = useState<string>('');
  const [types, setTypes] = useState<DocumentTypeSummary[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [type, setType] = useState<ReportType | ''>('');
  const [scope, setScope] = useState<RiskApplicationScope | ''>('');
  const [targets, setTargets] = useState<ScopeTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  // De qual alcance e empresa é a lista em `targets`: o texto de lista vazia só vale para ela.
  const [alvosCarregadosDe, setAlvosCarregadosDe] = useState<string | null>(null);
  const [recarregamentoDasAssinaturas, setRecarregamentoDasAssinaturas] = useState(0);
  const [scopeId, setScopeId] = useState<number | ''>('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [signatureBlock, setSignatureBlock] = useState<SignatureBlockItem[]>([]);
  const [attachmentIds, setAttachmentIds] = useState<number[]>([]);
  const [omittedAnnexBlocks, setOmittedAnnexBlocks] = useState<string[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState<DocumentFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prerequisite, setPrerequisite] = useState<BloqueioDaAcao | null>(null);
  const [changedPart, setChangedPart] = useState('');
  const [revisionDescription, setRevisionDescription] = useState('');
  const [manHours, setManHours] = useState('');
  // Ref, e não estado: o pré-preenchimento consulta, mas apagar o campo não pode refazer a consulta.
  const manHoursDigitadas = useRef(false);
  const [machineIds, setMachineIds] = useState<number[]>([]);

  const companyIdNumber = companyId === '' ? null : Number(companyId);
  const selectedType = useMemo(
    () => types.find((item) => item.type === type) ?? null,
    [types, type]
  );
  // useMemo para a identidade do array não mudar a cada render — sem isso o efeito
  // que pré-seleciona o alcance único dispararia em todo render.
  const allowedScopes = useMemo<RiskApplicationScope[]>(
    () => selectedType?.allowedScopes ?? [],
    [selectedType]
  );

  /**
   * A prévia e o texto escrito para ela andam juntos: parte alterada e descrição descrevem a
   * versão que AQUELA prévia anunciou. Mantê-los depois de mudar o pedido — ou depois de
   * emitir — faria o texto da 2.1 reaparecer e ser gravado na versão seguinte.
   */
  const descartarPrevia = useCallback((): void => {
    setPreview(null);
    setChangedPart('');
    setRevisionDescription('');
  }, []);

  // Empresa → tipos emissíveis. Tudo o que depende do tipo é zerado junto.
  useEffect(() => {
    if (companyIdNumber === null) {
      setTypes([]);
      setType('');
      return;
    }

    let ativo = true;
    setLoadingTypes(true);
    setError(null);

    documentService
      .listTypes(companyIdNumber)
      .then((lista) => {
        if (!ativo) return;
        setTypes(lista);
        setType('');
        setScope('');
        setScopeId('');
        setSignatureBlock([]);
        descartarPrevia();
      })
      .catch((err: unknown) => {
        if (!ativo) return;
        setError(mensagemDeErro(err, 'Não foi possível carregar os tipos de documento'));
      })
      .finally(() => {
        if (ativo) setLoadingTypes(false);
      });

    return () => {
      ativo = false;
    };
  }, [companyIdNumber, descartarPrevia]);

  // Tipo com um único alcance permitido já vem escolhido.
  useEffect(() => {
    if (allowedScopes.length === 1) {
      setScope(allowedScopes[0]);
    }
  }, [allowedScopes]);

  // Alcance → lista de alvos.
  useEffect(() => {
    if (companyIdNumber === null || scope === '') {
      setTargets([]);
      return;
    }

    if (scope === 'company') {
      setTargets([]);
      setScopeId(companyIdNumber);
      return;
    }

    let ativo = true;
    setLoadingTargets(true);
    setScopeId('');

    carregarAlvos(scope, companyIdNumber)
      .then((lista) => {
        if (!ativo) return;
        setTargets(lista);
        setAlvosCarregadosDe(`${scope}:${companyIdNumber}`);
      })
      .catch((err: unknown) => {
        if (!ativo) return;
        setError(mensagemDeErro(err, 'Não foi possível carregar as opções do alcance'));
      })
      .finally(() => {
        if (ativo) setLoadingTargets(false);
      });

    return () => {
      ativo = false;
    };
  }, [companyIdNumber, scope]);

  /**
   * Na volta de outra aba, o setor, cargo ou funcionário pode ter sido cadastrado lá. Separado
   * do efeito acima porque ele zera o alvo a cada carga — aqui o escolhido fica, se ainda existe.
   */
  const recarregarAlvos = useCallback((): void => {
    if (companyIdNumber === null || scope === '' || scope === 'company') return;

    carregarAlvos(scope, companyIdNumber)
      .then((lista) => {
        setTargets(lista);
        setAlvosCarregadosDe(`${scope}:${companyIdNumber}`);
        setScopeId((atual) =>
          atual !== '' && lista.some((alvo) => Number(alvo.id) === atual) ? atual : ''
        );
      })
      // Sem o recarregamento a lista de antes continua utilizável.
      .catch(() => undefined);
  }, [companyIdNumber, scope]);

  /**
   * Com as seis chaves preenchidas, traz as horas da última emissão igual (#420). Só preenche o
   * que o usuário não digitou; sem emissão igual, o campo volta vazio.
   */
  const pedeHorasHomem = selectedType?.pedeHorasHomem === true;

  // Horas digitadas ou trazidas para outras chaves não valem para estas. Roda antes da consulta
  // abaixo, então limpa o campo antes de a resposta das chaves novas chegar, nunca depois.
  useEffect(() => {
    setManHours('');
    manHoursDigitadas.current = false;
  }, [companyIdNumber, type, scope, scopeId, periodStart, periodEnd]);

  useEffect(() => {
    if (
      !pedeHorasHomem ||
      companyIdNumber === null ||
      type === '' ||
      scope === '' ||
      scopeId === '' ||
      !periodStart ||
      !periodEnd
    ) {
      return;
    }

    let ativo = true;
    documentService
      .getManHours({ companyId: companyIdNumber, type, scope, scopeId, periodStart, periodEnd })
      .then((horas) => {
        if (!ativo || manHoursDigitadas.current) return;
        setManHours(horas === null ? '' : String(horas));
      })
      // Sem o pré-preenchimento o campo continua utilizável: o usuário digita as horas.
      .catch(() => undefined);

    return () => {
      ativo = false;
    };
  }, [pedeHorasHomem, companyIdNumber, type, scope, scopeId, periodStart, periodEnd]);

  const pedeMaquinas = selectedType?.pedeMaquinas === true;
  const alcanceDeMaquinasPronto =
    pedeMaquinas &&
    companyIdNumber !== null &&
    (scope === 'company' || (scope === 'department' && scopeId !== ''));
  const setorDasMaquinas = scope === 'department' && scopeId !== '' ? scopeId : null;

  // Máquinas do alcance escolhido, pedidas só a quem pode ler máquinas. Sem a lista (recusa ou
  // falha) o campo fica vazio e o laudo sai com todas as máquinas ativas, como o texto diz.
  const carregarMaquinas = useCallback(
    (): Promise<readonly Machine[]> =>
      carregarMaquinasDoAlcance(companyIdNumber ?? 0, setorDasMaquinas),
    [companyIdNumber, setorDasMaquinas]
  );
  const listaDeMaquinas = useListaPermitida('machine.read', 'Máquinas', carregarMaquinas, {
    habilitado: alcanceDeMaquinasPronto,
  });
  const maquinas = listaDeMaquinas.itens;
  const carregandoMaquinas = listaDeMaquinas.carregando || !listaDeMaquinas.resolvida;
  const falhaAoCarregarMaquinas = listaDeMaquinas.erro !== undefined;
  const recarregarMaquinas = listaDeMaquinas.recarregar;
  const semMaquinasNoAlcance =
    !carregandoMaquinas && listaDeMaquinas.permitido && !falhaAoCarregarMaquinas && maquinas.length === 0;

  // A marcação é refeita junto com a lista: só sobrevive o que ainda existe.
  useEffect(() => {
    setMachineIds((marcadas) => {
      const mantidas = marcadas.filter((idMarcado) => maquinas.some((m) => Number(m.id) === idMarcado));
      return mantidas.length === marcadas.length ? marcadas : mantidas;
    });
  }, [maquinas]);

  const handleMachineToggle = (idMaquina: number, marcada: boolean): void => {
    setMachineIds((atuais) =>
      marcada ? [...atuais.filter((idAtual) => idAtual !== idMaquina), idMaquina] : atuais.filter((idAtual) => idAtual !== idMaquina)
    );
    descartarPrevia();
  };

  // O painel de declarações de EPI só consulta com alcance e período escolhidos.
  const pedeDeclaracoesEpi = selectedType?.pedeDeclaracoesEpi === true;
  const consultaDeDeclaracoesEpi = useMemo((): ConsultaDeclaracoesEpi | null => {
    if (scope === '' || scopeId === '' || !periodStart || !periodEnd) return null;
    return { scope, scopeId, periodStart, periodEnd };
  }, [scope, scopeId, periodStart, periodEnd]);

  const manHoursAbaixoDoMinimo = manHours !== '' && Number(manHours) < 1;

  const handleManHoursChange = (valor: string): void => {
    const digitos = valor.replace(/\D/g, '');
    setManHours(digitos);
    manHoursDigitadas.current = digitos !== '';
    descartarPrevia();
  };

  const handleTypeChange = (event: SelectChangeEvent<string | number>): void => {
    setType(String(event.target.value) as ReportType);
    setScope('');
    setScopeId('');
    // O bloco é recomposto pelo seletor com os papéis sugeridos do novo tipo.
    setSignatureBlock([]);
    descartarPrevia();
  };

  const handleScopeChange = (event: SelectChangeEvent<string | number>): void => {
    setScope(String(event.target.value) as RiskApplicationScope);
    setScopeId('');
    descartarPrevia();
  };

  const handleScopeIdChange = (event: SelectChangeEvent<string | number>): void => {
    setScopeId(Number(event.target.value));
    descartarPrevia();
  };

  /**
   * Estável (o seletor a usa como dependência de efeito). Zerar a prévia junto é
   * o que impede a geração de ser liberada por uma lista de pendências vencida.
   */
  const handleSignatureBlockChange = useCallback(
    (itens: SignatureBlockItem[]): void => {
      setSignatureBlock(itens);
      descartarPrevia();
    },
    [descartarPrevia]
  );

  /** Marcar ou desmarcar anexo muda o documento: a prévia mostrada deixa de valer. */
  const handleAttachmentsChange = useCallback(
    (ids: number[]): void => {
      setAttachmentIds(ids);
      descartarPrevia();
    },
    [descartarPrevia]
  );

  const handleOmittedAnnexBlocksChange = useCallback(
    (keys: string[]): void => {
      setOmittedAnnexBlocks(keys);
      descartarPrevia();
    },
    [descartarPrevia]
  );

  const handlePeriodStartChange = (valor: string): void => {
    setPeriodStart(valor);
    descartarPrevia();
  };

  const handlePeriodEndChange = (valor: string): void => {
    setPeriodEnd(valor);
    descartarPrevia();
  };

  /**
   * O pedido da prévia sem os textos de revisão, ou `null` enquanto falta campo obrigatório.
   * É o que a conferência manda: os textos de revisão não mudam o que falta para emitir.
   */
  const pedidoBase = useMemo((): EmissionRequest | null => {
    if (type === '' || scope === '' || scopeId === '' || !periodStart || !periodEnd) return null;

    const request: EmissionRequest = { type, scope, scopeId, periodStart, periodEnd, signatureBlock };

    // Só com anexo: o pedido sem anexo continua idêntico ao de antes (#520).
    if (attachmentIds.length > 0) request.attachmentIds = attachmentIds;
    // Só com anexo montado desmarcado: sem isso, o pedido é o mesmo de antes da #551.
    if (omittedAnnexBlocks.length > 0) request.omittedAnnexBlocks = omittedAnnexBlocks;
    // Só o documento com o quadro de acidentes leva horas; o servidor recusa as dos outros (#420).
    // Abaixo do mínimo não vão: o campo já avisa, e a geração recusa antes de pedir.
    if (pedeHorasHomem && manHours !== '' && !manHoursAbaixoDoMinimo) {
      request.manHours = Number(manHours);
    }
    // Sem marcação o pedido não leva a lista: o servidor usa todas as ativas do alcance.
    if (pedeMaquinas && machineIds.length > 0) request.machineIds = [...machineIds].sort((a, b) => a - b);

    return request;
  }, [
    type,
    scope,
    scopeId,
    periodStart,
    periodEnd,
    signatureBlock,
    attachmentIds,
    omittedAnnexBlocks,
    pedeHorasHomem,
    manHours,
    manHoursAbaixoDoMinimo,
    pedeMaquinas,
    machineIds,
  ]);

  const aoVoltarDeResolver = useCallback((): void => {
    recarregarAlvos();
    recarregarMaquinas();
    setRecarregamentoDasAssinaturas((atual) => atual + 1);
  }, [recarregarAlvos, recarregarMaquinas]);

  const verificacao = useVerificacaoDaEmissao(pedidoBase, aoVoltarDeResolver);
  const { marcarSaidaParaResolver, registrarResultado, reverificar, pedidoAindaVale, respostasAgora } =
    verificacao;
  const conferido = verificacao.estado === 'conferido' ? verificacao.resultado : null;

  /** Resposta gravada muda o conteúdo do laudo: a prévia mostrada e as pendências deixam de valer. */
  const aoSalvarDeclaracoesEpi = useCallback((): void => {
    descartarPrevia();
    reverificar();
  }, [descartarPrevia, reverificar]);

  /**
   * Parte alterada e descrição só existem quando a conferência mais recente — a verificação
   * ou a prévia — diz que a emissão abre uma versão
   * nova depois de outra: a 1.0 não tem o que descrever, e a versão reaproveitada (conteúdo
   * igual) não grava texto novo. Campo escondido não manda texto.
   *
   * Sem conferência de agora (falhou ou ainda confere), vale a última versão conhecida deste
   * mesmo pedido; e, sem nenhuma, o texto já digitado continua à vista e vai no pedido — sumir
   * com ele tiraria da emissão, em silêncio, o que a pessoa escreveu.
   */
  const semConferenciaDeAgora =
    verificacao.estado === 'falhou' || verificacao.estado === 'verificando';
  const versaoConhecida =
    conferido ?? (semConferenciaDeAgora ? verificacao.resultadoConhecido : null);
  const upcomingVersion = versaoConhecida?.upcomingVersion;
  const reaproveita = versaoConhecida?.reusesExisting === true;
  const textoDeRevisaoDigitado = changedPart.trim() !== '' || revisionDescription.trim() !== '';
  const descreveRevisao =
    (upcomingVersion !== undefined && upcomingVersion !== '1.0' && !reaproveita) ||
    (semConferenciaDeAgora && versaoConhecida === null && textoDeRevisaoDigitado);

  /** Devolve `null` (e publica a mensagem) quando falta algum campo obrigatório. */
  const buildRequest = useCallback((): EmissionRequest | null => {
    if (type === '' || scope === '' || scopeId === '') {
      setError('Escolha empresa, tipo de documento e alcance antes de gerar.');
      return null;
    }

    if (!periodStart || !periodEnd) {
      setError('Informe o período de vigência do documento.');
      return null;
    }

    if (pedeHorasHomem && manHoursAbaixoDoMinimo) {
      setError(MENSAGEM_HORAS_ABAIXO_DO_MINIMO);
      return null;
    }

    if (!pedidoBase) return null;
    const request: EmissionRequest = { ...pedidoBase };

    if (descreveRevisao) {
      if (changedPart.trim() !== '') request.changedPart = changedPart.trim();
      if (revisionDescription.trim() !== '') {
        request.revisionDescription = revisionDescription.trim();
      }
    }

    return request;
  }, [
    type,
    scope,
    scopeId,
    periodStart,
    periodEnd,
    pedeHorasHomem,
    manHoursAbaixoDoMinimo,
    pedidoBase,
    descreveRevisao,
    changedPart,
    revisionDescription,
  ]);

  const handlePreview = async (): Promise<void> => {
    const request = buildRequest();
    if (!request) return;
    // A prévia pode chegar depois de a pessoa mudar um campo: guardado no clique, o pedido
    // diz se a resposta ainda é deste formulário.
    const pedidoDoClique = verificacao.pedidoVigente;

    setPreviewing(true);
    setError(null);
    // Limpa o bloco de uma corrida anterior: a prévia nova já traz a lista de pendências
    // atualizada, então mantê-lo duplicaria a mesma informação na tela.
    setPrerequisite(null);

    try {
      const resultado = await documentService.preview(request);
      // A prévia confere o mesmo que a verificação: a lista dela passa a ser a mais recente.
      const aindaVale =
        pedidoDoClique !== null &&
        registrarResultado(pedidoDoClique, {
          pendencies: resultado.pendencies,
          blocked: resultado.pendencies.some((pendencia) => pendencia.severity === 'bloqueia'),
          upcomingVersion: resultado.upcomingVersion,
          reusesExisting: resultado.reusesExisting,
        });
      if (aindaVale) setPreview(resultado);
    } catch (err: unknown) {
      // A recusa de um pedido que já não vale fala de um formulário que mudou: some como o
      // sucesso da prévia antiga já some.
      if (pedidoDoClique === null || !pedidoAindaVale(pedidoDoClique)) return;
      setPreview(null);
      const bloqueio = prerequisiteFromError(err);
      if (bloqueio) {
        setPrerequisite({ bloco: bloqueio, respostasAteEntao: respostasAgora() });
      } else {
        setError(mensagemDeErro(err, 'Não foi possível gerar a prévia'));
      }
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async (format: DocumentFormat): Promise<void> => {
    const request = buildRequest();
    if (!request) return;

    setGenerating(format);
    setError(null);
    setPrerequisite(null);

    try {
      const arquivo = await documentService.generate(request, format);
      documentService.triggerDownload(arquivo);
      // A versão anunciada acabou de ser emitida: confere de novo para anunciar a seguinte.
      descartarPrevia();
      reverificar();
    } catch (err: unknown) {
      // A prévia sem bloqueio libera o botão, mas uma pendência pode surgir entre a
      // prévia e o clique — é a corrida que o servidor pega e este bloco cobre.
      const bloqueio = prerequisiteFromError(err);
      if (bloqueio) {
        setPrerequisite({ bloco: bloqueio, respostasAteEntao: respostasAgora() });
      } else {
        setError(mensagemDeErro(err, 'Não foi possível gerar o documento'));
      }
    } finally {
      setGenerating(null);
    }
  };

  const ocupado = generating !== null || previewing;

  /**
   * A geração só é liberada depois de uma conferência sem pendência bloqueante, ou quando a
   * conferência falhou por rede: aí o servidor recompõe tudo na geração e recusa o que impede.
   */
  const temBloqueio =
    conferido !== null &&
    (conferido.blocked || conferido.pendencies.some((pendencia) => pendencia.severity === 'bloqueia'));
  const geracaoTravada =
    temBloqueio ||
    verificacao.estado === 'incompleto' ||
    verificacao.estado === 'verificando' ||
    verificacao.estado === 'recusado';
  const motivoDosBotoes = temBloqueio ? MOTIVO_DO_BLOQUEIO : MOTIVO_DOS_BOTOES[verificacao.estado];

  // Um bloco de bloqueio por vez: o da prévia ou da geração vale até a conferência responder de
  // novo — a pessoa pode ter resolvido em outra aba —, e enquanto vale ele cobre a recusa dela.
  const bloqueioDaAcao =
    prerequisite !== null && prerequisite.respostasAteEntao === verificacao.respostas
      ? prerequisite.bloco
      : null;
  const recusaDaConferencia =
    bloqueioDaAcao === null && verificacao.estado === 'recusado' ? verificacao.recusa : null;

  // Mesma consulta da lista de pendências: a permissão que a API do cadastro exige.
  const cadastrarSetor = useAcao('department.create', 'Cadastrar setor');
  const cadastrarCargo = useAcao('occupation.create', 'Cadastrar cargo');
  const cadastrarFuncionario = useAcao('employee.create', 'Cadastrar funcionário');
  const podeCadastrarAlvo: Record<AlcanceComAlvo, boolean> = {
    department: !cadastrarSetor.disabled,
    occupation: !cadastrarCargo.disabled,
    employee: !cadastrarFuncionario.disabled,
  };
  const alcanceComAlvo: AlcanceComAlvo | null =
    scope === 'department' || scope === 'occupation' || scope === 'employee' ? scope : null;
  const vazioDoAlvo =
    alcanceComAlvo !== null &&
    !loadingTargets &&
    targets.length === 0 &&
    alvosCarregadosDe === `${alcanceComAlvo}:${companyIdNumber}`
      ? VAZIO_POR_ALCANCE[alcanceComAlvo]
      : null;
  const linkDoVazio = alcanceComAlvo !== null && podeCadastrarAlvo[alcanceComAlvo];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Dados da emissão
        </Typography>

        {/* Todo botão do bloco abre em outra aba — aqui navegar perderia o formulário —, e o
            clique em qualquer um marca a saída para resolver. */}
        {bloqueioDaAcao && (
          <Box sx={{ mb: 2 }} onClickCapture={marcarSaidaParaResolver}>
            <PrerequisiteBlock {...bloqueioDaAcao} openInNewTab />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <CompanySelectField
            value={companyId}
            onChange={setCompanyId}
            grid={{ xs: 12, md: 6 }}
          />

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth disabled={companyIdNumber === null || loadingTypes}>
              <InputLabel id="document-type-label">Tipo de documento *</InputLabel>
              <Select
                labelId="document-type-label"
                label="Tipo de documento *"
                value={type}
                onChange={handleTypeChange}
              >
                {types.map((item) => (
                  <MenuItem key={item.type} value={item.type}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {companyIdNumber !== null && !loadingTypes && types.length === 0 && (
              <FormHelperText>{SEM_TIPO_DE_DOCUMENTO}</FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth disabled={allowedScopes.length === 0}>
              <InputLabel id="document-scope-label">Alcance *</InputLabel>
              <Select
                labelId="document-scope-label"
                label="Alcance *"
                value={scope}
                onChange={handleScopeChange}
              >
                {allowedScopes.map((opcao) => (
                  <MenuItem key={opcao} value={opcao}>
                    {SCOPE_LABELS[opcao]}
                  </MenuItem>
                ))}
              </Select>
              {selectedType?.ajudaDoAlcance !== undefined && (
                <FormHelperText sx={{ fontSize: '1rem', lineHeight: 1.5, color: 'text.secondary' }}>
                  {selectedType.ajudaDoAlcance}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {scope !== '' && scope !== 'company' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={loadingTargets}>
                <InputLabel id="document-scope-id-label">{SCOPE_LABELS[scope]} *</InputLabel>
                <Select
                  labelId="document-scope-id-label"
                  label={`${SCOPE_LABELS[scope]} *`}
                  value={scopeId}
                  onChange={handleScopeIdChange}
                >
                  {targets.map((alvo) => (
                    <MenuItem key={alvo.id} value={alvo.id}>
                      {alvo.label}
                    </MenuItem>
                  ))}
                </Select>
                {vazioDoAlvo && (
                  <FormHelperText>
                    {vazioDoAlvo.texto}
                    {linkDoVazio && (
                      <>
                        {' '}
                        <Link
                          component="a"
                          href={vazioDoAlvo.rota}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abre em outra aba"
                          onClick={marcarSaidaParaResolver}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {vazioDoAlvo.rotulo}
                          <AbreEmOutraAbaIcon sx={{ fontSize: 12 }} />
                        </Link>
                      </>
                    )}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Início do período *"
              value={periodStart}
              onChange={(event) => handlePeriodStartChange(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Fim do período *"
              value={periodEnd}
              onChange={(event) => handlePeriodEndChange(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {pedeHorasHomem && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Horas-homem trabalhadas no período"
                placeholder="Ex.: 118.400"
                value={manHours}
                onChange={(event) => handleManHoursChange(event.target.value)}
                error={manHoursAbaixoDoMinimo}
                helperText={manHoursAbaixoDoMinimo ? MENSAGEM_HORAS_ABAIXO_DO_MINIMO : AJUDA_HORAS_HOMEM}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
            </Grid>
          )}

          {pedeMaquinas && alcanceDeMaquinasPronto && (
            <Grid size={{ xs: 12 }}>
              <FormControl component="fieldset" fullWidth>
                <Typography component="legend" variant="subtitle1">
                  Máquinas do laudo
                </Typography>
                {carregandoMaquinas && <CircularProgress size={20} aria-label="Carregando as máquinas" />}
                {!carregandoMaquinas && falhaAoCarregarMaquinas && (
                  <FormHelperText error>
                    Não foi possível carregar as máquinas.{' '}
                    <Button size="small" onClick={recarregarMaquinas}>
                      Tentar de novo
                    </Button>
                  </FormHelperText>
                )}
                {!carregandoMaquinas && !listaDeMaquinas.permitido && (
                  <FormHelperText>{listaDeMaquinas.bloqueio}</FormHelperText>
                )}
                {semMaquinasNoAlcance && (
                  <FormHelperText>
                    Nenhuma máquina ativa neste alcance. O laudo não pode ser emitido sem máquinas — cadastre uma em
                    Máquinas.
                  </FormHelperText>
                )}
                <FormGroup>
                  {maquinas.map((maquina) => (
                    <FormControlLabel
                      key={maquina.id}
                      sx={{ minHeight: 44 }}
                      control={
                        <Checkbox
                          checked={machineIds.includes(Number(maquina.id))}
                          onChange={(event) => handleMachineToggle(Number(maquina.id), event.target.checked)}
                        />
                      }
                      label={maquina.tag ? `${maquina.name} · ${maquina.tag}` : maquina.name}
                    />
                  ))}
                </FormGroup>
                <FormHelperText>
                  {machineIds.length > 0 ? AJUDA_MAQUINAS_SELECIONADAS : AJUDA_MAQUINAS_TODAS}
                  {scope === 'department' ? ` ${AJUDA_MAQUINAS_SEM_SETOR}` : ''}
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          {pedeDeclaracoesEpi && (
            <Grid size={{ xs: 12 }}>
              <DeclaracoesEpiPanel
                consulta={consultaDeDeclaracoesEpi}
                onAbrirLink={marcarSaidaParaResolver}
                onSalvo={aoSalvarDeclaracoesEpi}
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <SignatureBlockSelector
            companyId={companyIdNumber}
            type={type}
            value={signatureBlock}
            onChange={handleSignatureBlockChange}
            recarregarEm={recarregamentoDasAssinaturas}
            onAbrirLink={marcarSaidaParaResolver}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <AttachmentSelector
            companyId={companyIdNumber}
            type={type}
            value={attachmentIds}
            onChange={handleAttachmentsChange}
            omittedAnnexBlocks={omittedAnnexBlocks}
            onOmittedAnnexBlocksChange={handleOmittedAnnexBlocksChange}
          />
        </Box>

        {conferido && (
          <Box sx={{ mt: 3 }}>
            <PendencyList
              pendencies={conferido.pendencies}
              onAbrirLink={marcarSaidaParaResolver}
              subtitulo={temBloqueio ? 'Conferido agora' : 'Conferido agora · nada impede a emissão'}
            />
          </Box>
        )}

        {recusaDaConferencia && (
          <Box sx={{ mt: 3 }} onClickCapture={marcarSaidaParaResolver}>
            <PrerequisiteBlock {...recusaDaConferencia} openInNewTab />
          </Box>
        )}

        {/* Com item que impede, a lista acima já diz o que fazer: versão e revisão só aparecem
            quando há o que emitir. */}
        {!temBloqueio && (upcomingVersion !== undefined || descreveRevisao) && (
          <Box sx={{ mt: 2 }}>
            {upcomingVersion !== undefined && (
              <Typography variant="subtitle2">
                {reaproveita
                  ? `O conteúdo não mudou: a versão ${upcomingVersion} será reaproveitada`
                  : `Esta emissão sai como versão ${upcomingVersion}`}
              </Typography>
            )}
            {descreveRevisao && (
              <>
                <Typography variant="caption" color="text.secondary" display="block">
                  Opcionais. O que escrever aqui aparece no quadro de controle e revisões
                  do documento.
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Parte alterada"
                      value={changedPart}
                      onChange={(event) => setChangedPart(event.target.value)}
                      slotProps={{ htmlInput: { maxLength: 255 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Descrição da revisão"
                      value={revisionDescription}
                      onChange={(event) => setRevisionDescription(event.target.value)}
                      slotProps={{ htmlInput: { maxLength: 2000 } }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={
              previewing ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />
            }
            disabled={ocupado}
            onClick={() => void handlePreview()}
          >
            Gerar prévia
          </Button>
          <ActionButton
            acao="document_emission.generate"
            rotulo="Gerar PDF"
            variant="outlined"
            startIcon={
              generating === 'pdf' ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />
            }
            disabled={ocupado || geracaoTravada}
            onClick={() => void handleGenerate('pdf')}
          >
            Gerar PDF
          </ActionButton>
          <ActionButton
            acao="document_emission.generate"
            rotulo="Gerar DOCX"
            variant="outlined"
            startIcon={
              generating === 'docx' ? <CircularProgress size={18} color="inherit" /> : <DocxIcon />
            }
            disabled={ocupado || geracaoTravada}
            onClick={() => void handleGenerate('docx')}
          >
            Gerar DOCX
          </ActionButton>
        </Box>

        {motivoDosBotoes && (
          <Typography
            variant="caption"
            color={temBloqueio ? 'error' : 'text.secondary'}
            sx={{ display: 'block', mt: 1 }}
          >
            {motivoDosBotoes}
          </Typography>
        )}

        {preview && (
          <Box sx={{ mt: 3 }}>
            <DocumentPreview html={preview.html} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentEmissionForm;
